import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Maximize2 } from 'lucide-react';
import apiClient from '../services/apiService';

/**
 * The AnyGRC playbook, for signed-in users only. The HTML is fetched from an
 * authenticated endpoint (never a public file) and shown in a sandboxed iframe:
 * scripts run so the slide deck works, but it gets no access to the app's
 * origin, storage or token. The iframe takes the whole window so the slides
 * are the same size as the deck opened on its own; the only controls float.
 */
const PlaybookPage: React.FC = () => {
  const navigate = useNavigate();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/api/v1/utl/playbook', { responseType: 'text', transformResponse: (d) => d })
      .then((res) => { if (!cancelled) setHtml(res.data as string); })
      .catch(() => { if (!cancelled) setError('The playbook could not be loaded. Please try again.'); });
    return () => { cancelled = true; };
  }, []);

  const goFullScreen = () => {
    frameRef.current?.requestFullscreen?.().catch(() => undefined);
  };

  return (
    <div className="playbook-page">
      {html !== null ? (
        <iframe
          ref={frameRef}
          className="playbook-frame"
          title="AnyGRC Playbook"
          sandbox="allow-scripts"
          allow="fullscreen"
          srcDoc={html}
          data-testid="FRAME_playbook"
        />
      ) : (
        <div className="playbook-status">{error || 'Loading the playbook…'}</div>
      )}
      <div className="playbook-controls">
        <button className="playbook-btn" onClick={() => navigate('/')} title="Back to AnyGRC" data-testid="BTN_playbook_back">
          <ArrowLeft size={15} /> AnyGRC
        </button>
        {html !== null && (
          <button className="playbook-btn" onClick={goFullScreen} title="Full screen" data-testid="BTN_playbook_fullscreen">
            <Maximize2 size={14} /> Full screen
          </button>
        )}
      </div>
    </div>
  );
};

export default PlaybookPage;
